import { useSnackbar } from "notistack";
import {
  jobMessage,
  jobMessageList,
  updateMessageSeen,
} from "../endpoints/job.service";
import { EnumUserTypes, userTypes } from "../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  fetchJobMessagesProfile,
  fetchUnseenMessageCount,
} from "../store/actions/message.action";
import { useSocket } from "context/SocketProvider";
import { v4 as uuidv4 } from "uuid";

const LIMIT = 50;

const MESSAGE_EVENTS = {
  SEND_MESSAGE: "job:chat:send",
  RECEIVE_MESSAGE: "job:chat:receive",
  TYPING: "job:chat:typing",
};

const useChat = ({ jobId, receiverDetails, fetchInbox = false }) => {
  const dispatch = useDispatch();
  const socketRef = useSocket();
  const socket = socketRef?.current;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state) => state.auth);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false)
  const [nextCursor, setNextCursor] = useState(null)
  const [messages, setMessages] = useState([]);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isMessageLoading, setIsMessageLoading] = useState(true);
  const [isOlderMessageLoading, setIsOlderMessageLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  async function loadOlderMessages() {
    setIsOlderMessageLoading(true);
    jobMessageList({
      jobId,
      receiverId: receiverDetails?.userId,
      cursor: nextCursor
    })
      .then((response) => {
        setHasNextPage(response?.data?.pagination?.hasNextPage)
        setNextCursor(response?.data?.pagination?.nextCursor)
        // setPage((prev) => prev + 1);
        const olderMessages = response?.data?.messages?.reverse();
        console.log(
          "check older messages",
          olderMessages,
          response?.data?.messages,
        );
        setMessages((prev) => {
          return [...olderMessages, ...prev];
        });
      })
      .catch((error) => {
        console.error("❌ useChat: Failed to fetch older message list", error);
      })
      .finally(() => {
        setIsOlderMessageLoading(false);
      });
  }

  // Send message handler
  async function sendMessage({ message, setMessage }) {
    if (!message.trim() || !receiverDetails) return;
    try {
      setIsMessageSending(true);

      try {
        const chatId = uuidv4();
        const payload = {
          chatId,
          content: message,
          jobId,
          recipientId: receiverDetails?.userId,
        };
        console.log('send message payload', payload)
        socket.emit(MESSAGE_EVENTS.SEND_MESSAGE, payload);
        // Add temporary message to UI immediately
        setMessages((prev) => {
          return [
            ...prev,
            {
              chatId,
              message,
              createdName: user?.firstName + " " + user?.lastName,
              seen: false,
              createdBy: user?._id || "",
              isTemp: true,
            },
          ];
        });
        // console.log("Socket send message payload", receiverDetails, payload);
      } catch (error) {
        console.log(error, "logging here error in temp message emit");
      }

      setMessage("");
    } catch (error) {
      enqueueSnackbar("Error sending message: " + error.message, {
        variant: "error",
      });
    } finally {
      setIsMessageSending(false);
    }
  }

  function triggerTyping(status) {
    console.log("Typing triggered");
    const payload = {
      jobId,
      recipientId: receiverDetails?.userId,
      typing: status,
    };
    socket.emit(MESSAGE_EVENTS.TYPING, payload);
  }

  useEffect(() => {
    if (!socket) return;

    const handleTempMessage = (msg) => {
      const isSameJobId = msg?.jobApplicationId === jobId;
      const isCurrentUsersMessage = msg?.createdBy === user?._id;

      if (isSameJobId && isCurrentUsersMessage) {
        setMessages((prev) => {
          return [
            ...prev,
            {
              chatId: msg.chatId,
              message: msg.message,
              createdName: msg.createdName,
              seen: msg.seen,
              createdBy: msg.createdBy,
              isTemp: msg.isTemp,
            },
          ];
        });
      }
    };

    socket.on("tempMessage", handleTempMessage);

    return () => {
      socket.off("tempMessage", handleTempMessage);
    };
  }, [socket, jobId]);

  // Socket Listener with comprehensive debugging
  useEffect(() => {
    if (!socket) {
      // console.log("❌ useChat: No socket available - cannot set up listeners");
      return;
    }

    const handleNewMessage = (messageData) => {
      console.log("useChat: New message", messageData, jobId);
      // Always refresh message profile
      dispatch(fetchJobMessagesProfile());

      // 🔥 CRITICAL: Improved message filtering logic
      const isSameJobId = messageData?.jobId === jobId; //jobApplicationId is jobId

      // Determine the correct receiver ID to compare with
      const receiverIdToCompare = receiverDetails?.userId;

      const isFromReceiver = messageData?.createdBy === receiverIdToCompare;

      if (isSameJobId && isFromReceiver) {
        // console.log("✅ useChat: Message matches current chat - ADDING TO UI");
        const newMessage = {
          messageId: messageData?._id,
          message: messageData?.message,
          createdName: receiverDetails?.name,
          createdAt: messageData?.createdAt,
          seen: messageData?.seen,
          createdBy: messageData?.createdBy,
          receivedBy: messageData?.receivedBy,
        };
        setMessages((prev) => [...prev, newMessage]);
      } else {
        // console.log("🚫 useChat: Message filtered out - not for this chat");
      }
    };

    const handleTyping = (data) => {
      console.log("Typing received", data);
      if (data?.jobId === jobId) {
        setIsTyping(data?.typing);
      }
    };

    const handleMessageRead = (messageResponse) => {
      // console.log("👀 useChat: messageReadByReceiver", {
      //   messageId: messageResponse?._id,
      //   seen: messageResponse?.seen,
      // });

      setMessages((prev) => {
        return prev?.map((item) => {
          if (item?.messageId === messageResponse?._id) {
            // console.log(
            //   "✅ useChat: Marking message as read",
            //   messageResponse?._id,
            // );
            return { ...item, seen: true };
          } else {
            return item;
          }
        });
      });
    };

    // Debug socket connection state
    const handleConnect = () => {
      // console.log("✅ useChat: Socket connected", socket.id);
    };

    const handleDisconnect = (reason) => {
      // console.log("🔌 useChat: Socket disconnected", reason);
    };

    const handleConnectError = (error) => {
      // console.error("❌ useChat: Socket connection error", error.message);
    };

    // Remove existing listeners to avoid duplicates
    socket.off(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
    socket.off(MESSAGE_EVENTS.TYPING, handleTyping);
    // socket.off("tempMessage", handleNewMessage);
    socket.off("messageReadByReceiver", handleMessageRead);
    socket.off("connect", handleConnect);
    socket.off("disconnect", handleDisconnect);
    socket.off("connect_error", handleConnectError);

    // Add new listeners
    // socket.on("newMessage", handleNewMessage);
    socket.on(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
    socket.on(MESSAGE_EVENTS.TYPING, handleTyping);
    // socket.on("tempMessage", handleNewMessage);
    socket.on("messageReadByReceiver", handleMessageRead);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    // console.log("✅ useChat: Socket listeners setup complete");

    // Cleanup on component unmount or dependency change
    return () => {
      // console.log("🧹 useChat: Cleaning up socket listeners");
      socket.off(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
      socket.off(MESSAGE_EVENTS.TYPING, handleTyping);
      socket.off("messageReadByReceiver", handleMessageRead);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [socket, jobId, receiverDetails, user?._id, dispatch]);

  // Fetching list of messages
  useEffect(() => {
    console.log('Fetching params', jobId, receiverDetails)
    if (jobId && receiverDetails) {
      setPage(1);
      setIsMessageLoading(true);
      jobMessageList({
        receiverId: receiverDetails?.userId,
        jobId,
        limit: LIMIT,
      })
        .then((response) => {
          setHasNextPage(response?.data?.pagination?.hasNextPage)
          setNextCursor(response?.data?.pagination?.nextCursor)
          const formattedData = response?.data?.data?.map((item)=>{
           return {
            ...item,
            createdBy:item?.jobTitle,
            message:item?.createdBy,
            receivedBy:item?.message
           }
          })
          setMessages(formattedData?.reverse() || []);
        })
        .catch((error) => {
          // console.error("❌ useChat: Failed to fetch message list", error);
        })
        .finally(() => {
          setIsMessageLoading(false);
        });
    } else {
      // console.log(
      //   "🚫 useChat: Skipping message fetch - missing jobApplicationId or receiverDetails",
      // );
    }
  }, [receiverDetails, jobId]);

  // Mark messages as seen
  useEffect(() => {
    if (fetchInbox) {
      // console.log("📨 useChat: Fetching inbox messages");
      dispatch(fetchJobMessagesProfile());
      return;
    }

    if (messages?.length > 0) {
      const unseenMessages = messages?.filter(
        (item) =>
          !item?.seen &&
          item?.createdBy !==
          (user?.userType === userTypes.RECRUITER ? user?._id : user?.userId),
      );

      if (unseenMessages.length > 0) {
        // console.log("👀 useChat: Marking messages as seen", {
        //   unseenMessages,
        //   count: unseenMessages.length,
        //   messageIds: unseenMessages.map((m) => m.messageId),
        // });

        const unseenMessageIds = unseenMessages
          .map((message) => message?._id || message?.messageId)
          .filter((id) => id); // Filter out temporary message IDs

        if (unseenMessageIds.length > 0) {
          updateMessageSeen({ messagesIds: unseenMessageIds })
            .then(() => {
              // console.log("✅ useChat: Messages marked as seen");
              dispatch(fetchJobMessagesProfile());
              dispatch(fetchUnseenMessageCount());
            })
            .catch((error) => {
              // console.error(
              //   "❌ useChat: Failed to mark messages as seen",
              //   error,
              // );
            });
        }
      }
    }
  }, [messages, fetchInbox, user?.userType, user?._id, user?.userId, dispatch]);

  // console.log("💬 useChat: Current state", {
  //   messageCount: messages.length,
  //   isLoading: isMessageLoading,
  //   isSending: isMessageSending,
  //   hasSocket: !!socket,
  //   socketConnected: socket?.connected,
  // });

  return {
    isMessageLoading,
    messages,
    isMessageSending,
    sendMessage,
    loadOlderMessages,
    isOlderMessageLoading,
    triggerTyping,
    isTyping,
    hasNextPage
  };
};

export default useChat;
